/**
 * Scale functions for mapping data values to pixel positions.
 * Provides linear and time scales without external dependencies.
 */

import type { Scale } from "~types/types";

/**
 * Linear interpolation between two values.
 */
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Inverse linear interpolation.
 */
function inverseLerp(a: number, b: number, value: number): number {
  // Avoid division by zero
  if (a === b) return 0.5;

  return (value - a) / (b - a);
}

/**
 * Clamp value between min and max.
 */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Options for creating a linear scale.
 */
export interface LinearScaleOptions {
  /** [min, max] data values */
  domain: [number, number];
  /** [min, max] pixel positions */
  range: [number, number];
  /** Clamp output to range bounds, default: false */
  clamp?: boolean;
}

/**
 * Create a linear scale that maps domain values to pixel range.
 */
export function createLinearScale(options: LinearScaleOptions): Scale {
  const { clamp: shouldClamp = false, range } = options;
  const { domain } = options;

  const [domainMin, domainMax] = domain;
  const [rangeMin, rangeMax] = range;

  return {
    domain: () => domain,
    range: () => range,

    scale: (value: number): number => {
      // Handle edge case: zero-length domain
      if (domainMin === domainMax) {
        return (rangeMin + rangeMax) / 2;
      }

      const t = inverseLerp(domainMin, domainMax, value);
      const pixel = lerp(rangeMin, rangeMax, t);

      return shouldClamp
        ? clamp(
            pixel,
            Math.min(rangeMin, rangeMax),
            Math.max(rangeMin, rangeMax)
          )
        : pixel;
    },

    invert: (pixel: number): number => {
      // Handle edge case: zero-length range
      if (rangeMin === rangeMax) {
        return (domainMin + domainMax) / 2;
      }

      const t = inverseLerp(rangeMin, rangeMax, pixel);
      const value = lerp(domainMin, domainMax, t);

      if (shouldClamp) {
        return clamp(
          value,
          Math.min(domainMin, domainMax),
          Math.max(domainMin, domainMax)
        );
      }

      return value;
    },
  };
}

/**
 * Options for creating a time scale.
 */
export interface TimeScaleOptions {
  /** [startMs, endMs] timestamps */
  domain: [number, number];
  /** [min, max] pixel positions */
  range: [number, number];
  /** Clamp output to range bounds */
  clamp?: boolean;
}

/**
 * Create a time scale (specialized linear scale for timestamps).
 */
export function createTimeScale(options: TimeScaleOptions): Scale {
  // Time scale is just a linear scale semantically
  return createLinearScale(options);
}

/**
 * Add percentage buffer to domain.
 */
export function domainWithBuffer(
  domain: [number, number],
  bufferRatio = 0.15
): [number, number] {
  const [min, max] = domain;

  if (min === max) {
    // If domain is a single point, create a small range around it
    const buffer = Math.abs(min) * bufferRatio || 1;

    return [min - buffer, max + buffer];
  }

  const range = max - min;
  const buffer = range * bufferRatio;

  return [min - buffer, max + buffer];
}

/**
 * Check if a new value requires rescaling.
 */
export function shouldRescale(
  newValue: number,
  currentDomain: [number, number],
  hysteresisRatio = 0.05
): boolean {
  const [min, max] = currentDomain;
  const range = max - min;
  const threshold = range * hysteresisRatio;

  return newValue < min + threshold || newValue > max - threshold;
}
